import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import {
	useEntryGroupsCache,
	useMattersCache,
	useRestrictedWords,
	useSettingsCache,
} from "@/shared/cache";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import type { Entry } from "@/shared/types/entries";
import { incrementForMinimumUnits } from "@/shared/utils/units";
import type { ActionFormSubmit } from "@/v2/components/action-dialog/ActionForm";
import type { CollapseDialogSubmit } from "@/v2/components/CollapseDialog";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import {
	DEFAULT_FILTER,
	type FilterState,
} from "@/v2/components/FilterPopover";
import {
	DEFAULT_GROUP,
	DEFAULT_SORT,
	type GroupMode,
	type SortMode,
} from "@/v2/components/SortPopover";
import { useActions } from "@/v2/contexts/EntriesActionsProvider";
import { useTimerContext } from "@/v2/contexts/TimerProvider";
import type { CollapseOptions } from "@/v2/hooks/actions/collapse";
import type { GroupOptions } from "@/v2/hooks/actions/group";
import type { MergeOptions } from "@/v2/hooks/actions/merge";
import type { ApiError } from "@/v2/hooks/actions/types";
import { useDateParams } from "@/v2/hooks/useDateParams";
import { useRubberBand } from "@/v2/hooks/useRubberBand";
import { formatDateForApi } from "@/v2/lib/dates";
import { entryGroupEntryToLegacy } from "@/v2/lib/entryAdapters";
import {
	createGroupOptions,
	createMergeOptions,
} from "@/v2/lib/entryBulkActions";
import { createAddEntryPayload } from "@/v2/lib/entryPayloads";
import { hasConflicts } from "@/v2/lib/reconcileSources";
import {
	MIXED_TIMEKEEPER_SELECTION_MESSAGE,
	selectionTimekeeperId,
} from "@/v2/lib/selectionTimekeeperId";
import { AddEntryDialog } from "./components/AddEntryDialog";
import { EntriesActionDock } from "./components/EntriesActionDock";
import { EntriesDateToolbar } from "./components/EntriesDateToolbar";
import { EntriesList } from "./components/EntriesList";
import { EntriesSelectionOverlay } from "./components/EntriesSelectionOverlay";
import { EntryWorkflowDialogs } from "./components/EntryWorkflowDialogs";
import { useEntriesAutoScroll } from "./hooks/useEntriesAutoScroll";
import { useEntriesPageData } from "./hooks/useEntriesPageData";
import { useEntriesShellContainer } from "./hooks/useEntriesShellContainer";
import type { EntryFieldConflict } from "./types";

export default function Entries() {
	useDocumentTitle("Entries");

	const { date, dateRange, setDate, setDateRange } = useDateParams();
	const { matters } = useMattersCache();
	const { entries: entryGroups, isLoading } = useEntryGroupsCache(matters);
	const entries = useMemo(
		() =>
			entryGroups.map(entryGroupEntryToLegacy).filter((entry) => entry.id > 0),
		[entryGroups],
	);
	const actions = useActions();
	const timer = useTimerContext();
	const { getRestrictedWords } = useRestrictedWords();
	const { settings } = useSettingsCache();
	const { pageRef, shellContainer, handlePageRef } = useEntriesShellContainer();

	const [addOpen, setAddOpen] = useState(false);
	const [groupEntries, setGroupEntries] = useState<Entry[] | null>(null);
	const [mergeEntries, setMergeEntries] = useState<Entry[] | null>(null);
	const [collapseEntries, setCollapseEntries] = useState<Entry[] | null>(null);
	const [groupConflicts, setGroupConflicts] = useState<
		EntryFieldConflict[] | undefined
	>(undefined);
	const [mergeConflicts, setMergeConflicts] = useState<
		EntryFieldConflict[] | undefined
	>(undefined);
	const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
	const [sort, setSort] = useState<SortMode>(DEFAULT_SORT);
	const [group, setGroup] = useState<GroupMode>(DEFAULT_GROUP);

	useRubberBand(pageRef);

	const { dateFilteredEntries, table, filteredRows, isMultiDate, selection } =
		useEntriesPageData({
			entries,
			date,
			dateRange,
			filter,
			sort,
			group,
			matters,
			getRestrictedWords,
		});

	const handleEntriesRendered = useEntriesAutoScroll({
		date,
		dateRange,
		filteredEntryCount: filteredRows.length,
		pageRef,
	});

	const timerPrefillDuration = timer.pendingDuration;
	const addDialogOpen = addOpen || timerPrefillDuration !== null;
	const selectedHasPendingMutation = selection.selectedIds.some((id) =>
		actions.pendingEntryIds.has(id),
	);
	const selectedEntryGroups = useMemo(() => {
		const groupById = new Map(entryGroups.map((entry) => [entry.id, entry]));
		return selection.selectedEntries
			.map((entry) =>
				entry.entryGroupId ? groupById.get(entry.entryGroupId) : undefined,
			)
			.filter((entry): entry is (typeof entryGroups)[number] => Boolean(entry));
	}, [entryGroups, selection.selectedEntries]);
	const selectedSourceEntryCount = useMemo(
		() =>
			selectedEntryGroups.reduce(
				(count, entry) => count + entry.miniTransactions.length,
				0,
			),
		[selectedEntryGroups],
	);
	const hideMoveToNewGroupRow =
		selection.selectedCount > 0 &&
		selectedSourceEntryCount > 1 &&
		selectedEntryGroups.length <= 1;
	const moveToNewGroupDisabledReason = useMemo(() => {
		if (selection.selectedCount === 0) return "Select entries to move";
		if (selectedSourceEntryCount < 2)
			return "Select at least two entries to move";
		if (selectedHasPendingMutation) return "Selection is already updating";
		if (selectionTimekeeperId(selection.selectedEntries) === false) {
			return MIXED_TIMEKEEPER_SELECTION_MESSAGE;
		}
		if (
			selectedEntryGroups.some(
				(entry) => entry.status === "exported" || entry.status === "deleted",
			)
		) {
			return "Exported or deleted groups can't be moved";
		}
		if (
			selectedEntryGroups.length === 0 ||
			selectedEntryGroups.every((entry) => entry.miniTransactions.length === 0)
		) {
			return "Selected entries are no longer available";
		}
		return null;
	}, [
		selectedEntryGroups,
		selectedSourceEntryCount,
		selectedHasPendingMutation,
		selection.selectedCount,
		selection.selectedEntries,
	]);

	const clearTimerPrefill = useCallback(() => {
		timer.clearPendingDuration();
	}, [timer]);

	const resetAddDialogState = useCallback(() => {
		clearTimerPrefill();
	}, [clearTimerPrefill]);

	const handleAddOpenChange = useCallback(
		(open: boolean) => {
			setAddOpen(open);
			if (!open) resetAddDialogState();
		},
		[resetAddDialogState],
	);

	const closeGroupDialog = useCallback((open: boolean) => {
		if (!open) {
			setGroupEntries(null);
			setGroupConflicts(undefined);
		}
	}, []);

	const closeMergeDialog = useCallback((open: boolean) => {
		if (!open) {
			setMergeEntries(null);
			setMergeConflicts(undefined);
		}
	}, []);

	const closeCollapseDialog = useCallback((open: boolean) => {
		if (!open) setCollapseEntries(null);
	}, []);

	const handleAddSubmit = useCallback(
		(values: TimeEntryFormValues) => {
			const dateString = formatDateForApi(values.date);

			if (timerPrefillDuration !== null) {
				setAddOpen(true);
				timer.dismiss();
				clearTimerPrefill();
			}

			actions.addEntry(createAddEntryPayload(values, dateString), () =>
				setAddOpen(false),
			);
		},
		[actions, clearTimerPrefill, timer, timerPrefillDuration],
	);

	const handleAddClick = useCallback(() => {
		setAddOpen(true);
	}, []);

	const handleExport = useCallback(() => {
		if (selection.selectedIds.length === 0) return;
		const timekeeperId = selectionTimekeeperId(selection.selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		actions.exportEntries(selection.selectedEntries, timekeeperId);
		selection.exitSelectionMode();
	}, [actions, selection]);

	const dispatchMerge = useCallback(
		(ids: number[], options: MergeOptions, sources: Entry[]) => {
			const timekeeperId = selectionTimekeeperId(sources);
			if (timekeeperId === false) {
				toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
				return;
			}
			actions.mergeEntries(
				ids,
				options,
				(err: ApiError) => {
					if (
						err.code === "FIELD_CONFLICT" &&
						err.conflicts &&
						err.conflicts.length > 0
					) {
						setMergeConflicts(err.conflicts);
						setMergeEntries(sources);
					}
				},
				timekeeperId,
			);
		},
		[actions],
	);

	const dispatchGroup = useCallback(
		(ids: number[], options: GroupOptions, sources: Entry[]) => {
			const timekeeperId = selectionTimekeeperId(sources);
			if (timekeeperId === false) {
				toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
				return;
			}
			actions.groupEntries(
				ids,
				options,
				(err: ApiError) => {
					if (
						err.code === "FIELD_CONFLICT" &&
						err.conflicts &&
						err.conflicts.length > 0
					) {
						setGroupConflicts(err.conflicts);
						setGroupEntries(sources);
					}
				},
				timekeeperId,
			);
		},
		[actions],
	);

	const handleGroup = useCallback(() => {
		if (selection.selectedIds.length < 2) return;
		setGroupConflicts(undefined);

		const increment = incrementForMinimumUnits(settings.minimum_units);

		if (!hasConflicts(selection.selectedEntries, "group", increment)) {
			dispatchGroup(
				selection.selectedIds,
				createGroupOptions({
					entries: selection.selectedEntries,
					increment,
					includeUnits: settings.p_group_include_units,
				}),
				selection.selectedEntries,
			);
			selection.exitSelectionMode();
			return;
		}

		setGroupEntries(selection.selectedEntries);
	}, [
		dispatchGroup,
		selection,
		settings.p_group_include_units,
		settings.minimum_units,
	]);

	const handleMerge = useCallback(() => {
		if (selection.selectedIds.length < 2) return;
		setMergeConflicts(undefined);

		const increment = incrementForMinimumUnits(settings.minimum_units);

		if (!hasConflicts(selection.selectedEntries, "merge", increment)) {
			dispatchMerge(
				selection.selectedIds,
				createMergeOptions({
					entries: selection.selectedEntries,
					increment,
				}),
				selection.selectedEntries,
			);
			selection.exitSelectionMode();
			return;
		}

		setMergeEntries(selection.selectedEntries);
	}, [dispatchMerge, selection, settings.minimum_units]);

	const handleCollapse = useCallback(() => {
		if (selection.selectedIds.length < 2) return;
		setCollapseEntries(selection.selectedEntries);
	}, [selection.selectedEntries, selection.selectedIds.length]);

	const handleDuplicate = useCallback(() => {
		if (selection.selectedIds.length === 0) return;
		const timekeeperId = selectionTimekeeperId(selection.selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		actions.duplicateEntries(selection.selectedIds, timekeeperId);
		selection.exitSelectionMode();
	}, [actions, selection]);

	const handleDelete = useCallback(() => {
		if (selection.selectedIds.length === 0) return;
		const timekeeperId = selectionTimekeeperId(selection.selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		actions.deleteEntries(selection.selectedIds, timekeeperId);
		selection.exitSelectionMode();
	}, [actions, selection]);

	const handleMoveSelectionToNewGroup = useCallback(() => {
		if (selection.selectedEntries.length === 0 || moveToNewGroupDisabledReason)
			return;
		const timekeeperId = selectionTimekeeperId(selection.selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		actions.moveEntriesToNewGroup(selection.selectedEntries, timekeeperId);
		selection.exitSelectionMode();
	}, [actions, moveToNewGroupDisabledReason, selection]);

	const handleGroupSubmit = useCallback(
		(values: ActionFormSubmit) => {
			const sources = groupEntries ?? [];
			const ids = sources.map((entry) => entry.id);
			const options: GroupOptions = {
				date: formatDateForApi(values.date),
				matterId: values.matterId,
				taskId: values.taskId,
				activityId: values.activityId,
				unitsSeconds: values.unitsSeconds,
				includeUnits: values.includeUnits ?? settings.p_group_include_units,
			};

			dispatchGroup(ids, options, sources);
			setGroupEntries(null);
			setGroupConflicts(undefined);
			selection.exitSelectionMode();
		},
		[dispatchGroup, groupEntries, selection, settings.p_group_include_units],
	);

	const handleMergeSubmit = useCallback(
		(values: ActionFormSubmit) => {
			const sources = mergeEntries ?? [];
			const ids = sources.map((entry) => entry.id);
			const options: MergeOptions = {
				date: formatDateForApi(values.date),
				matterId: values.matterId,
				taskId: values.taskId,
				activityId: values.activityId,
				unitsSeconds: values.unitsSeconds,
			};

			dispatchMerge(ids, options, sources);
			setMergeEntries(null);
			setMergeConflicts(undefined);
			selection.exitSelectionMode();
		},
		[dispatchMerge, mergeEntries, selection],
	);

	const handleCollapseSubmit = useCallback(
		({ mainSourceId, ...values }: CollapseDialogSubmit) => {
			const sources = collapseEntries ?? [];
			const ids = sources.map((entry) => entry.id);
			const options: CollapseOptions = {
				date: formatDateForApi(values.date),
				matterId: values.matterId,
				taskId: values.taskId,
				activityId: values.activityId,
				unitsSeconds: values.unitsSeconds,
			};

			const timekeeperId = selectionTimekeeperId(sources);
			if (timekeeperId === false) {
				toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
				return;
			}
			actions.collapseEntries(
				ids,
				mainSourceId,
				options,
				undefined,
				timekeeperId,
			);
			setCollapseEntries(null);
			selection.exitSelectionMode();
		},
		[actions, collapseEntries, selection],
	);

	return (
		<div ref={handlePageRef} className="relative flex min-h-full flex-col">
			<EntriesSelectionOverlay
				selectionMode={selection.selectionMode}
				shellContainer={shellContainer}
				selectedCount={selection.selectedCount}
				allCurrentPageSelected={selection.allCurrentPageSelected}
				someCurrentPageSelected={selection.someCurrentPageSelected}
				onSelectAll={selection.selectAll}
				onClose={selection.exitSelectionMode}
			/>

			<EntriesDateToolbar
				date={date}
				dateRange={dateRange}
				onDateChange={setDate}
				onDateRangeChange={setDateRange}
				filter={filter}
				onFilterChange={setFilter}
				sort={sort}
				group={group}
				onSortChange={setSort}
				onGroupChange={setGroup}
				entries={dateFilteredEntries}
				matters={matters}
				getRestrictedWords={getRestrictedWords}
			/>

			<div className="flex-1">
				<EntriesList
					isLoading={isLoading}
					entries={entries}
					dateFilteredEntries={dateFilteredEntries}
					table={table}
					isMultiDate={isMultiDate}
					selectionMode={selection.selectionMode}
					matters={matters}
					getRestrictedWords={getRestrictedWords}
					onIconClick={selection.handleIconClick}
					onRowSelect={selection.handleRowSelect}
					onLongPress={selection.enterSelectionMode}
					onFilterChange={setFilter}
					onEntriesRendered={handleEntriesRendered}
					newGroupAction={
						selection.selectionMode &&
						selection.selectedCount > 0 &&
						!hideMoveToNewGroupRow
							? {
									disabledReason: moveToNewGroupDisabledReason,
									onClick: handleMoveSelectionToNewGroup,
								}
							: null
					}
				/>
			</div>

			<AddEntryDialog
				open={addDialogOpen}
				date={date}
				timerPrefillDuration={timerPrefillDuration}
				onOpenChange={handleAddOpenChange}
				onSubmit={handleAddSubmit}
			/>

			<EntryWorkflowDialogs
				groupEntries={groupEntries}
				groupConflicts={groupConflicts}
				mergeEntries={mergeEntries}
				mergeConflicts={mergeConflicts}
				collapseEntries={collapseEntries}
				defaultIncludeUnits={settings.p_group_include_units}
				onGroupOpenChange={closeGroupDialog}
				onMergeOpenChange={closeMergeDialog}
				onCollapseOpenChange={closeCollapseDialog}
				onGroupSubmit={handleGroupSubmit}
				onMergeSubmit={handleMergeSubmit}
				onCollapseSubmit={handleCollapseSubmit}
			/>

			<EntriesActionDock
				selectionMode={selection.selectionMode}
				selectedCount={selection.selectedCount}
				bulkDisabled={selectedHasPendingMutation}
				onMerge={handleMerge}
				onCollapse={handleCollapse}
				onGroup={handleGroup}
				onDuplicate={handleDuplicate}
				onDelete={handleDelete}
				onExport={handleExport}
				onAdd={handleAddClick}
			/>
		</div>
	);
}
